/**
 * Suggestion Box — the ONE copy of the triage vocabulary the admin speaks.
 *
 * STATUS_OPTIONS mirrors the `suggestions.status` CHECK constraint exactly
 * (new · reviewing · planned · shipped · declined). The list tabs, the per-status
 * counts, the show Pill and the edit Select all read this array, so a sixth status
 * is one edit here plus the migration — never a second hand-typed list that drifts.
 *
 * Tones are per-status Pills, NOT theme/columns' renderStatus: brand.statusColor's
 * regex knows none of these words, so 'new', 'reviewing' and 'planned' would all
 * paint the same neutral grey and the queue would lose its one useful signal —
 * which suggestions nobody has looked at yet (gold, like the Reports 'open' pill).
 */
import type { Tone } from '../../theme/listkit'

export const SUGGESTION_STATUSES = ['new', 'reviewing', 'planned', 'shipped', 'declined'] as const

export type SuggestionStatus = (typeof SUGGESTION_STATUSES)[number]

export const STATUS_OPTIONS: { value: SuggestionStatus; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'planned', label: 'Planned' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'declined', label: 'Declined' },
]

const TONE: Record<SuggestionStatus, Tone> = {
  new: 'gold', // nobody has triaged it yet — the one bucket that needs an admin
  reviewing: 'jaguar',
  planned: 'jaguar',
  shipped: 'green',
  declined: 'neutral', // a closed answer, not an error — never red
}

export function statusTone(status: string | null | undefined): Tone {
  return TONE[status as SuggestionStatus] ?? 'neutral'
}

/** The human label for a status; an unknown value (a status added server-side
 * before this file) is shown raw rather than hidden. */
export function statusLabel(status: string | null | undefined): string {
  return STATUS_OPTIONS.find((o) => o.value === status)?.label ?? String(status ?? '—')
}
